import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

export const useOnlineIds = () => {
  const { API, user } = useContext(AuthContext);
  const [ids, setIds] = useState(new Set());

  useEffect(() => {
    if (!user) {
      setIds(new Set());
      return undefined;
    }
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const fetchIds = async () => {
      try {
        const res = await axios.get(`${API}/presence/online`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIds(new Set((res.data.online || []).map((u) => u.id)));
      } catch {
        // silent
      }
    };
    fetchIds();
    const id = setInterval(fetchIds, 30000);
    return () => clearInterval(id);
  }, [API, user]);

  return ids;
};

const OnlineUsers = ({ onUserClick, compact = false }) => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [online, setOnline] = useState([]);

  useEffect(() => {
    if (!user) return undefined;
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const fetchOnline = async () => {
      try {
        const res = await axios.get(`${API}/presence/online`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOnline((res.data.online || []).filter((u) => u.id !== user.id));
      } catch {
        // silent
      }
    };
    fetchOnline();
    const id = setInterval(fetchOnline, 30000);
    return () => clearInterval(id);
  }, [API, user]);

  if (!user || online.length === 0) return null;

  const handleClick = (u) => {
    if (onUserClick) onUserClick(u);
    else navigate(`/messages?chat=${u.id}&name=${encodeURIComponent(u.full_name)}`);
  };

  return (
    <div className={compact ? '' : 'max-w-5xl mx-auto px-4'} data-testid="online-users-bar">
      <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm p-4 brand-font">
        <div className="flex items-center gap-2 mb-3">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10b47e] opacity-60"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#10b47e]"></span>
          </span>
          <span className="text-sm font-bold text-[#1e2b5e] dark:text-slate-100">מחוברים עכשיו</span>
          <span className="text-xs font-bold text-[#10b47e] bg-[#10b47e]/10 px-2 py-0.5 rounded-full">{online.length}</span>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-1 scrollbar-hide">
          {online.map((u) => (
            <button
              key={u.id}
              onClick={() => handleClick(u)}
              className="flex flex-col items-center gap-1.5 shrink-0 group w-16"
              data-testid={`online-user-${u.id}`}
              title={`שלח הודעה ל${u.full_name}`}
            >
              <div className="relative">
                <div className="rounded-full p-[2px] bg-gradient-to-br from-[#10b47e] to-emerald-400 group-hover:scale-105 transition-transform">
                  <Avatar className="w-12 h-12 border-2 border-white dark:border-slate-900">
                    {u.avatar && <AvatarImage src={u.avatar} alt={u.full_name} />}
                    <AvatarFallback className="bg-gradient-to-br from-[#1d6ff2] to-cyan-400 text-white font-bold text-sm">
                      {u.full_name?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <span className="absolute bottom-0 left-0 w-3.5 h-3.5 bg-[#10b47e] rounded-full border-2 border-white dark:border-slate-900"></span>
              </div>
              <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 truncate w-full text-center group-hover:text-[#1d6ff2] transition-colors">
                {u.full_name?.split(' ')[0]}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OnlineUsers;
